import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CreateCircle.css';

const PLANS = [
  { id: 'circle_3',  label: 'Circle 3',  price: 79,  seats: 3  },
  { id: 'circle_5',  label: 'Circle 5',  price: 129, seats: 5  },
  { id: 'circle_10', label: 'Circle 10', price: 199, seats: 10 },
];

const CreateCircle = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('circle_5');
  const [circleName, setCircleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if user already has a circle — redirect to manage
  useEffect(() => {
    const checkExisting = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/circles/mine`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.circle?._id) {
            navigate(`/circles/${data.circle._id}/manage`);
          }
        }
      } catch (err) {
        // No circle — stay on create page
      }
    };

    // Only check if not returning from checkout
    const params = new URLSearchParams(window.location.search);
    if (!params.get('checkout')) {
      checkExisting();
    }
  }, []);

  // Check on mount if returning from successful checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      const plan = params.get('plan');
      const circleName = params.get('circleName');
      if (plan && circleName) {
        // Auto-create the circle after successful payment
        createCircle(plan, circleName);
      }
    }
  }, []);

  const createCircle = async (plan, name) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/circles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ name: name.trim(), plan }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to create Circle.');
        return;
      }
      navigate(`/circles/${data.circle._id}/manage`);
    } catch (err) {
      console.error('[CreateCircle]', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!circleName.trim()) {
      setError('Please enter a name for your Circle.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Go through Stripe checkout
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/billing/create-circle-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ plan: selectedPlan, circleName: circleName.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.message || 'Failed to start checkout.');
        return;
      }
      // Redirect to Stripe
      window.location.href = data.url;
    } catch (err) {
      console.error('[CreateCircle]', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="circle-page">
      <div className="circle-container">

        {/* Header */}
        <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>

        <div className="circle-header">
          <div className="circle-sparkle">✦</div>
          <h1>Create a Circle</h1>
          <p>A shared workspace for your team — clients, notes, and sessions in one place.</p>
        </div>

        {/* Plan Picker */}
        <div className="plan-section">
          <h2 className="section-label">Choose your plan</h2>
          <div className="plan-grid">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                <div className="plan-price">${plan.price}</div>
                <div className="plan-period">/mo</div>
                <div className="plan-seats">Up to {plan.seats} members</div>
                <div className="plan-label">{plan.label}</div>
                {selectedPlan === plan.id && (
                  <div className="plan-check">✓</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Circle Name */}
        <div className="name-section">
          <h2 className="section-label">Name your Circle</h2>
          <input
            type="text"
            className="circle-name-input"
            placeholder="e.g. Technolica Sales Team"
            value={circleName}
            onChange={(e) => setCircleName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            maxLength={100}
            disabled={loading}
          />
          <p className="input-hint">This is what your team members will see when they receive an invite.</p>
        </div>

        {/* What's included */}
        <div className="features-section">
          <h2 className="section-label">What's included — everything in Pro, plus:</h2>
          <div className="features-grid">
            <div className="feature-item">
              <span className="feature-icon">👥</span>
              <div>
                <strong>Shared clients</strong>
                <p>Share client records, notes, and session history with your team</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">✦</span>
              <div>
                <strong>AI insights for everyone</strong>
                <p>Transcriptions and AI summaries visible to all Circle members</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔔</span>
              <div>
                <strong>Team notifications</strong>
                <p>Get notified when teammates add notes or schedule sessions</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔒</span>
              <div>
                <strong>You control sharing</strong>
                <p>Only clients you explicitly share are visible to the team</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💎</span>
              <div>
                <strong>All Pro features</strong>
                <p>Unlimited clients, AI Persona, video conferencing, calendar sync</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">👤</span>
              <div>
                <strong>Free for members</strong>
                <p>Teammates join at no extra cost — billing stays with the owner</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="circle-error">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* CTA */}
        <button
          className="create-circle-btn"
          onClick={handleCreate}
          disabled={loading || !circleName.trim()}
        >
          {loading ? (
            <span className="btn-loading">
              <span className="spinner" /> {window.location.search.includes('checkout=success') ? 'Creating your Circle...' : 'Redirecting to checkout...'}
            </span>
          ) : (
            `Continue to checkout — $${PLANS.find(p => p.id === selectedPlan)?.price}/mo`
          )}
        </button>

        <p className="billing-note">
          Secure checkout via Stripe. Cancel anytime. All Pro features included.
        </p>

      </div>
    </div>
  );
};

export default CreateCircle;
